import { Star } from 'lucide-react';

import { useShopContent } from '@/features/storefront/content/useContent';
import { cn } from '@/lib/cn';

/**
 * Five stars, filled to `value` - halves and all, so a 4.3 reads as a little
 * over four rather than rounding to a claim of five.
 *
 * Drawn in the theme's primary colour on a muted track, which is what lets the
 * same stars sit on every theme. The label is the caller's, because only the
 * caller knows the words: "4.3 out of 5" under a card, "4 out of 5" on one
 * review. Without one the stars are decoration and hidden from screen readers.
 */
export function StarRating({
  value,
  size = 16,
  label,
  className,
}: {
  value: number;
  /** Pixels per star. */
  size?: number;
  label?: string;
  className?: string;
}) {
  const clamped = Math.max(0, Math.min(5, value));

  return (
    <span
      className={cn('inline-flex shrink-0 items-center gap-0.5 align-middle', className)}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      {Array.from({ length: 5 }, (_, index) => {
        // How much of this star is lit: 0, 1, or the part in between.
        const fill = Math.max(0, Math.min(1, clamped - index));
        return (
          <span key={index} className="relative inline-block" style={{ width: size, height: size }}>
            <Star
              className="absolute inset-0"
              style={{ width: size, height: size, color: 'var(--storefront-muted)', opacity: 0.35 }}
              fill="currentColor"
              strokeWidth={0}
              aria-hidden
            />
            {fill > 0 ? (
              <span className="absolute inset-y-0 start-0 overflow-hidden" style={{ width: `${fill * 100}%` }}>
                <Star
                  className="block max-w-none"
                  style={{ width: size, height: size, color: 'var(--storefront-primary)' }}
                  fill="currentColor"
                  strokeWidth={0}
                  aria-hidden
                />
              </span>
            ) : null}
          </span>
        );
      })}
    </span>
  );
}

/**
 * The stars and the count, in one line: under a product card, beside a price.
 *
 * Nothing at all for a product nobody has reviewed - five empty stars read as
 * "rated badly", which is a worse thing to say about a new product than
 * nothing. The whole line is read out as one sentence ("4.5 out of 5 stars from
 * 12 reviews") rather than as a picture and two numbers.
 */
export function RatingSummary({
  average,
  count,
  size = 14,
  className,
}: {
  average: number | null | undefined;
  count: number | null | undefined;
  size?: number;
  className?: string;
}) {
  const copy = useShopContent();
  if (!count || count <= 0 || average == null) return null;
  const label = copy('reviews.summary_label', { rating: average.toFixed(1), count });

  return (
    <span className={cn('inline-flex items-center gap-1.5', className)} role="img" aria-label={label}>
      <StarRating value={average} size={size} />
      <span aria-hidden className="text-xs" style={{ color: 'var(--storefront-muted)' }}>
        {average.toFixed(1)} ({count})
      </span>
    </span>
  );
}

/** The element id the product page's reviews section stands under. */
export const REVIEWS_ANCHOR = 'reviews';

/**
 * The same line, as a way down to the reviews - beside the price on the
 * product page.
 *
 * A link to `#reviews` that scrolls rather than jumps, and only when there is
 * a reviews section to scroll to: a merchant who has not added one still gets
 * the stars, and a click on them does nothing rather than rewriting the URL.
 */
export function RatingLink({
  average,
  count,
  className,
}: {
  average: number | null | undefined;
  count: number | null | undefined;
  className?: string;
}) {
  if (!count || count <= 0 || average == null) return null;
  return (
    <a
      href={`#${REVIEWS_ANCHOR}`}
      className={cn('inline-flex text-sm font-normal no-underline', className)}
      onClick={(event) => {
        const target = document.getElementById(REVIEWS_ANCHOR);
        event.preventDefault();
        target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }}
    >
      <RatingSummary average={average} count={count} />
    </a>
  );
}
